<template>
  <div
    :id="_uid"
    v-click-outside="fold"
    class="tw-relative tw-w-full tw-outline-none"
    :class="{ disabled, error: !validity, noValue: !selectedLabel, isUnfolded }"
    data-test-id="VSelect"
    tabindex="0"
    @focus="setupKeystrokeListener"
    @blur="deleteKeystrokeListener"
  >
    <VLabel v-if="$slots.label || label" class="tw-mb-2">
      <template #default>
        <slot name="label">{{ label }}</slot>
      </template>
    </VLabel>
    <!-- TODO: turn this into a <select> element and change the label into an actual <label> using LabelInput -->
    <div
      class="selectInput tw-w-full tw-cursor-pointer tw-rounded-xs tw-border tw-border-solid tw-border-gray-300 tw-bg-white tw-p-4"
      data-test-id="toggleFold"
      :class="{ disabled }"
      @click="toggleFold"
    >
      <span v-if="selectedLabel" class="ellipsis">
        <Base400Black class="tw-align-middle">{{ selectedLabel }}</Base400Black>
      </span>
      <span v-else-if="placeholder" class="ellipsis tw-align-middle">
        <Base400Gray400 class="tw-align-middle">
          {{ placeholder }}
        </Base400Gray400>
      </span>
      <transition name="scale" mode="out-in">
        <i
          v-if="isUnfolded"
          key="up"
          class="fas fa-chevron-up tw-ml-1 tw-text-base tw-text-discrete2"
        />
        <span
          v-else-if="modelValue && withReset"
          class="tw-text-base tw-text-discrete2"
        >
          <i class="fas fa-times" @click.stop="resetValue" />
        </span>
        <i
          v-else
          key="down"
          class="fas fa-chevron-down tw-ml-2xs tw-text-base tw-text-discrete2"
        />
      </transition>
    </div>
    <transition name="fade" mode="out-in">
      <div
        v-if="isUnfolded"
        class="optionsWrapper tw-w-full tw-cursor-pointer tw-rounded-xs tw-bg-white tw-p-4"
      >
        <div
          v-for="(option, index) in options"
          :id="`Vselect_${option.label}_${index}`"
          :key="index"
          class="optionElem"
          data-test-id="option"
          @click.stop="handleOptionClick(option)"
        >
          <Base400Black>{{ option.label }}</Base400Black>
        </div>
      </div>
    </transition>
    <HeightExpand>
      <div
        v-if="$slots['error-message'] && !validity"
        class="tw-mt-1 tw-text-sm tw-text-error"
      >
        <slot name="error-message" />
      </div>
    </HeightExpand>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import { vClickOutside } from '~/directives/ClickOutside'

import HeightExpand from '~/components/transitions/heightExpand.vue'
import { Base400Black, Base400Gray400 } from '~/components/typography'
import VLabel from '~/components/ui/VLabel.vue'

import validationChildMixin from '~/mixins/validationChild'

import getDereferencedValue from '~/helpers/getDereferencedValue'

import type { PropType } from 'vue'

interface Option {
  label: string
  value: number | string
}
type Data = number | string | Record<string, any>

export default defineComponent({
  components: {
    Base400Black,
    Base400Gray400,
    HeightExpand,
    VLabel,
  },
  directives: {
    clickOutside: vClickOutside,
  },
  mixins: [validationChildMixin],
  props: {
    available: {
      type: Array as PropType<Data[]>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    labelKey: {
      type: [Array, String] as PropType<string[] | string>,
      default(): string[] {
        return []
      },
    },
    placeholder: {
      type: String,
      default: '',
    },
    transformLabelFn: {
      type: Function as PropType<(value: string) => string>,
      default: (value: string): string => {
        return value
      },
    },
    modelValue: {
      type: [String, Number, null] as PropType<number | string | null>,
      validator(): boolean {
        return true
      },
      default(): null {
        return null
      },
    },
    valueKey: {
      type: [Array, String] as PropType<string[] | string>,
      default(): string[] {
        return []
      },
    },
    withReset: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      isUnfolded: false,
      hasKeystrokesWatcher: false,
      keystrokeRecordBuffer: [] as string[],
      keystrokeRecordTimer: 1500,
      keystrokeRecordPurgeTimeout: null as null | number,
      // TODO this is very not cool practice! consider using useId ?
      // https://nuxt.com/docs/api/composables/use-id
      _uid: '',
    }
  },
  computed: {
    selectedLabel(): string | undefined {
      if (this.modelValue !== null) {
        return this.options.find(
          (option: Option): boolean => option.value === this.modelValue,
        )?.label
      }

      return undefined
    },
    options(): Option[] {
      return this.available.map((element: Data) => this.createOption(element))
    },
    requiredDereferencing(): boolean {
      return (this.valueKey?.length ?? 0) > 0
    },
  },
  mounted() {
    this._uid = this.makeId()
  },
  methods: {
    setupKeystrokeListener(): void {
      if (!this.hasKeystrokesWatcher && this.isUnfolded) {
        window.addEventListener('keydown', this.keyStrokeController)
        this.hasKeystrokesWatcher = true
      }
    },
    deleteKeystrokeListener(): void {
      window.removeEventListener('keydown', this.keyStrokeController)
      this.hasKeystrokesWatcher = false
    },
    keyStrokeController(e: KeyboardEvent): void {
      if (e.key.length === 1) {
        this.keystrokeRecordBuffer.push(e.key)
        this.keystrokeRecordComparator()
      }
      this.keyStrokePurgeTimer()
      this.keystrokeRecordPurgeTimeout = window.setTimeout(() => {
        this.keystrokeRecordBuffer.splice(0, this.keystrokeRecordBuffer.length)
      }, this.keystrokeRecordTimer)
    },
    keyStrokePurgeTimer(): void {
      if (this.keystrokeRecordPurgeTimeout !== null)
        window.clearTimeout(this.keystrokeRecordPurgeTimeout)
    },
    keystrokeRecordComparator(): void {
      const firstMatchIndex = this.options.findIndex((option) => {
        return this.keystrokeRecordBuffer.every(
          (letter, index) =>
            option.label.toLowerCase()[index] === letter.toLowerCase(),
        )
      })

      if (firstMatchIndex !== -1) {
        const firstMatch = this.options[firstMatchIndex]
        const targetId = `Vselect_${firstMatch.label}_${firstMatchIndex}`

        document.getElementById(targetId)?.scrollIntoView()
      } else {
        this.keyStrokePurgeTimer()
        this.keystrokeRecordBuffer.splice(0, this.keystrokeRecordBuffer.length)
      }
    },
    handleOptionClick(option: Option): void {
      this.$emit('update:modelValue', option.value)
      if (option.value !== this.modelValue) this.$emit('change')

      this.fold()
    },
    createOption(element: Data): Option {
      return {
        label: this.getLabel(element),
        value: this.getValue(element),
      }
    },
    getLabel(element: Data): string {
      let value: string
      if (this.requiredDereferencing && this.labelKey?.length > 0) {
        value =
          getDereferencedValue(this.labelKey, element as Record<string, any>) ??
          ''
      } else {
        value = this.getValue(element)?.toString() ?? ''
      }

      return this.transformLabelFn(value)
    },
    getValue(element: Data): string | number {
      if (this.requiredDereferencing) {
        return getDereferencedValue(
          this.valueKey,
          element as Record<string, any>,
        )
      } else {
        return element as string | number
      }
    },
    toggleFold(): void {
      if (this.disabled) return

      this.isUnfolded = !this.isUnfolded
      if (this.isUnfolded) this.setupKeystrokeListener()
      else this.deleteKeystrokeListener()
    },
    fold(): void {
      this.isUnfolded = false
      this.deleteKeystrokeListener()
    },
    resetValue(): void {
      this.$emit('update:modelValue', null)
    },
  },
})
</script>
<style lang="scss" scoped>
.selectInput {
  @apply tw-relative tw-flex tw-h-12 tw-items-center tw-justify-between;

  &.disabled {
    @apply tw-pointer-events-none tw-bg-gray-300 tw-opacity-75;
  }
}

.error > .selectInput {
  @apply tw-border-error;
}

.optionsWrapper {
  @apply tw-absolute tw-overflow-y-auto tw-p-0 tw-shadow;
  max-height: 160px;
  top: calc(100% + 8px);
  z-index: 999;
}

.optionElem {
  @apply tw-p-3 tw-pl-4 tw-transition tw-duration-150 tw-ease-in;
  &:hover {
    @apply tw-bg-gray-200 tw-duration-100 tw-ease-out;
  }
}

.noValue .selectInput > span {
  @apply tw-text-gray-500;
}
</style>
